import { Injectable } from '@angular/core';
import { LotteryService } from './lottery.service';
import { ShareType } from '../model/interfaces/share-type';
import { TicketPackage } from '../model/interfaces/ticket-package';
import { Order } from '../model/interfaces/order';
import { Fee } from '../model/interfaces/fee';
import { LotteryActuality } from '../model/enums/lottery-actuality';
import { combineLatest, Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { LotteryClassTicketPrices } from '../model/interfaces/lottery-class-ticket-prices';
import { LotteryClassTicketPrice } from '../model/interfaces/lottery-class-ticket-price';
import { NetOrderType } from '../model/enums/net-order-type';
import { LotteryClass } from '../model/interfaces/lottery-class';
import { OrderPrice } from '../model/interfaces/order-price';

@Injectable({ providedIn: 'root' })
export class PriceService
{
    private readonly lotteryService: LotteryService;

    public constructor(lotteryService: LotteryService)
    {
        this.lotteryService = lotteryService;
    }

    /**
     * Gets the total price of an order split into a base price and a list of fees.
     * @param order
     */
    public getPriceByOrder(order: Order): Observable<OrderPrice>
    {
        const tasks: Observable<LotteryClassTicketPrices>[] = [];
        
        switch (order.NetOrderType)
        {
            case NetOrderType.Subscription:
                tasks.push.apply(tasks, order.Shares.map(share => { return this.getPriceByShareType(share.Type); }));
                tasks.push.apply(tasks, order.TicketPackageInstances.map(instance => { return this.getPriceByPackage(instance.TicketPackage); }));

                break;

            case NetOrderType.SpecificMonthTransaction:
                tasks.push.apply(tasks, order.Instances.map(ticketInstance => { return this.getPriceByShareType(ticketInstance.Share.Type); }));

                break;
        }

        if (tasks.length === 0)
        {
            return of(
                { 
                    fmtPrice: 0, 
                    pricePrMonth: 0, 
                    priceByLotteryClass: [], 
                    totalPrice: 0, 
                    fees: [] 
                });
        }
        
        return combineLatest(tasks).pipe(map(prices => {
            
            var result = prices.reduce((acc: { 
                fmtPrice: number, 
                pricePrMonth: number,
                priceByLotteryClass: {lotteryClass: LotteryClass, price: number }[],
                totalPrice: number, 
                fees: Fee[] 
            }, current) => {
                acc.pricePrMonth += current.subscriptionSingleMonth;
                acc.totalPrice += order.NumberOfClasses === 6 ? current.subscriptionSixMonths : current.subscriptionSingleMonth;
                acc.fmtPrice += current.fmt.ticketPrice;
                acc.priceByLotteryClass = current.subscriptionPriceList.map(
                    c => { 
                        let item = acc.priceByLotteryClass.find(pbl => pbl.lotteryClass.Id === c.lotteryClass.Id) || { lotteryClass: c.lotteryClass, price: 0 };  
                        item.price += c.ticketPrice;
                        return item;
                    });

                return acc;
            },
            {
                pricePrMonth: 0,
                totalPrice: 0,
                priceByLotteryClass: [],
                fees: this.getFeesByOrder(order),
                fmtPrice: 0
            });

            return result;
        }));
    }

    public getPriceByShareType(type: ShareType): Observable<LotteryClassTicketPrices>
    {
         return this.getBasePrices().pipe(map((priceClasses: LotteryClassTicketPrices) => {
             
            if (priceClasses == null)
            {
                return null;
            }

            const priceSubscriptionList = priceClasses.subscriptionPriceList.map(pc => { return { lotteryClass: pc.lotteryClass, ticketPrice: pc.ticketPrice * type.Size }; });
            const priceFmt = priceClasses.fmt != null ? { lotteryClass: priceClasses.fmt.lotteryClass, ticketPrice: priceClasses.fmt.ticketPrice * type.Size } : null;

            return {
                subscriptionPriceList: priceSubscriptionList,
                fmt: priceFmt,
                subscriptionSingleMonth: priceClasses.subscriptionSingleMonth * type.Size,
                subscriptionSixMonths: priceClasses.subscriptionSixMonths * type.Size
            };
        }));
     }

    public getPriceByPackage(ticketPackage: TicketPackage): Observable<LotteryClassTicketPrices>
    {
        const tasks = [];
        ticketPackage.TicketPackageContents.forEach(
            contents => {
                for (let i = 0; i < contents.NumberOfTickets; i++)
                {
                    tasks.push(this.getPriceByShareType(contents.TicketShareType));
                }
            });

        return combineLatest<LotteryClassTicketPrices[]>(tasks).pipe(map(p => {

            // Reduce shares price lists
            const prices = p.reduce((accumulatedPrices: LotteryClassTicketPrices, currentPrices: LotteryClassTicketPrices) => {
                const fmt = accumulatedPrices.fmt || (accumulatedPrices.fmt = { lotteryClass: currentPrices.fmt.lotteryClass, ticketPrice: 0 });
                fmt.ticketPrice += currentPrices.fmt.ticketPrice;
                accumulatedPrices.subscriptionPriceList = currentPrices.subscriptionPriceList.map((subscriptionPrice, idx) => {
                    return {
                        lotteryClass: subscriptionPrice.lotteryClass,
                        ticketPrice: subscriptionPrice.ticketPrice + (accumulatedPrices.subscriptionPriceList[idx] != null ? accumulatedPrices.subscriptionPriceList[idx].ticketPrice : 0 )
                    };
                });
                accumulatedPrices.subscriptionSingleMonth += currentPrices.subscriptionSingleMonth;
                accumulatedPrices.subscriptionSixMonths += currentPrices.subscriptionSixMonths;
                
                return accumulatedPrices;
            }, { fmt: null, subscriptionPriceList: [], subscriptionSingleMonth: 0, subscriptionSixMonths: 0});

            return prices;
        }));
     }

    /**
     * Gets the total price of an SMT order.
     * @param order
     */
    public getPriceBySmtOrder(order: Order): Observable<number>
    {
        if(order.Instances.length === 0 && order.Shares.length === 0)
        {
            return of(0);
        }

        if(order.Instances.length !== 0)
        {
            const tasks = order.Instances.map(instance => {
                return this.getPriceByShareType(instance.Share.Type).pipe(map(p => { return p.fmt.ticketPrice; }));
            });
            return combineLatest(tasks).pipe(map(prices => {
                return prices.reduce((sum, currentPrice) => { return sum + currentPrice; }, 0);
            }));
        }

        if(order.Shares.length !== 0)
        {
            const tasks = order.Shares.map(share => {
                return this.getPriceByShareType(share.Type).pipe(map(p => { return p.fmt.ticketPrice; }));
            });
            return combineLatest(tasks).pipe(map(prices => {
                return prices.reduce((sum, currentPrice) => { return sum + currentPrice; }, 0);
            }));
        }
    
    }

    /**
     * Gets all included fees for a specific order.
     * @param order the instance of order to calculate fees for.
     */
    public getFeesByOrder(order: Order): Fee[]
    {
        if (order.NumberOfClasses === 1)
        {
            return [{ name: 'bs-gebyr', value: 500 }];
        }

        return [];
    }

    public getTotalFees(order: Order): number
    {
        const fees = this.getFeesByOrder(order).map(a => a.value);
        const totalFees = fees.reduce((a, b) => a + b, 0);

        return totalFees;
    }

    private getBasePrices(): Observable<LotteryClassTicketPrices>
    {
        const actualities = LotteryActuality.Current + LotteryActuality.Next + LotteryActuality.Future;

        return this.lotteryService.getLotteriesByActuality(LotteryActuality.Current + LotteryActuality.Next).pipe(map(
            lotteries => {

                if (lotteries == null)
                {
                    return null;
                }

                // Reads the full ticket price for each lottery and associates each class with that price.
                const classPrices = lotteries.reduce<LotteryClassTicketPrice[]>((acc, lottery) => { return [...acc, ...lottery.LotteryClasses.map(c => { return { lotteryClass: c, ticketPrice: lottery.FullTicketPrice }; })];}, []);
                const classPricesSubscription = classPrices.filter(c => { return (actualities ^ c.lotteryClass.ActualityByBSSale) < actualities; });
                classPricesSubscription.sort((c1, c2) => { return +new Date(c1.lotteryClass.InternetLastSale) - +new Date(c2.lotteryClass.InternetLastSale);  } );
                classPricesSubscription.length = 6; // Only take the 6 firstcoming months as is the max subscription period.
                const classPriceFmt = classPrices.find(c => { return (actualities ^ c.lotteryClass.ActualityByDKSale) < actualities; });
                
                return {
                    subscriptionPriceList: classPricesSubscription,
                    fmt: classPriceFmt,
                    subscriptionSingleMonth: classPricesSubscription[0].ticketPrice,
                    subscriptionSixMonths: classPricesSubscription.reduce((acc, cp) => { return acc + cp.ticketPrice; }, 0)
                };
            }
        ));
    }

    public getPriceChangeInEffectStatus(priceData: OrderPrice): boolean
    {
        const priceByLotteryClass = priceData.priceByLotteryClass;
        const priceAverage = priceByLotteryClass.reduce((acc, next) => { return acc + next.price }, 0) / priceByLotteryClass.length;
        // If the average price for the selected price is different than the 1st upcoming price. Price change is in effect.
        return priceByLotteryClass[0] ? priceAverage !== priceByLotteryClass[0].price : false;
    }

}
